'use client';

import {
  SearchEngine,
  buildUrlManager,
  loadPaginationActions,
  loadQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/atomic-react';
import { PAGE_SIZE_OPTIONS } from '@lib/constants/coveo';
import { dispatchCoveoUrlChange } from '@lib/coveo/urlChangeEvent';
import { useEffect, useRef } from 'react';

/**
 * Translates legacy CategoryFacet URLs (`cf-{field}={seg1},{seg2}`) to the
 * flat Facet format (`f-{field}={encoded "seg1 > seg2"}`) that buildFacet uses.
 *
 * Example: `cf-ec_category=3d,characters` → `f-ec_category=3d%20%3E%20characters`
 */
function translateCfFragment(fragment: string): string {
  return fragment.replace(/\bcf-([^=&]+)=([^&]*)/g, (_, field, rawValue) => {
    try {
      const value = decodeURIComponent(rawValue);
      const flatValue = value
        .split(',')
        .map((s) => s.trim())
        .join(' > ');
      return `f-${field}=${encodeURIComponent(flatValue)}`;
    } catch {
      return `f-${field}=${rawValue}`;
    }
  });
}

/**
 * Strips `numberOfResults` from a Coveo URL fragment.
 *
 * The Coveo UrlManager's internal state comes from getCoreActiveSearchParameters,
 * which deliberately excludes numberOfResults. As a result, when the page loads
 * with numberOfResults in the hash (e.g. from a legacy URL redirect), the manager
 * computes a fragment without it, sees a mismatch, and calls pushState to remove it.
 * In Next.js SPA navigation mode this pushState can re-trigger the effect with the
 * cleaned-up hash, causing restoreSearchParameters to fall back to
 * defaultNumberOfResults (24) and overwrite the URL-supplied value.
 *
 * By stripping numberOfResults before handing the fragment to buildUrlManager we
 * prevent the spurious pushState. The value is applied separately via
 * registerNumberOfResults so that defaultNumberOfResults is also updated, making
 * any subsequent restoreSearchParameters call fall back to the correct value.
 */
function stripNumberOfResults(fragment: string): string {
  return fragment
    .replace(/(?:^|&)numberOfResults=[^&]*/g, '')
    .replace(/^&/, '');
}

/**
 * Dispatches `registerNumberOfResults` when `fragment` contains a valid
 * `numberOfResults` value. Called on both initial load and popstate navigation
 * because Coveo's UrlManager never serializes `numberOfResults`, so
 * `urlManager.synchronize` alone won't restore it on back/forward navigation.
 */
function applyNumberOfResultsFromFragment(
  fragment: string,
  eng: SearchEngine
): void {
  const value = parseInt(
    new URLSearchParams(fragment).get('numberOfResults') ?? '',
    10
  );
  if (PAGE_SIZE_OPTIONS.includes(value)) {
    eng.dispatch(loadPaginationActions(eng).registerNumberOfResults(value));
  }
}

/**
 * Wires up Coveo's UrlManager to the browser's history API and handles
 * back/forward navigation. Dispatches the initial search on first call.
 *
 * @param eng - The Coveo engine to sync. Pass null when not yet initialized.
 * @param readLocalStorage - When true, falls back to coveo-standalone-search-box-data
 *   in localStorage to seed the initial query (used on the /search route when
 *   arriving from the header search box and there is no URL hash present).
 *   The `q` parameter from the URL hash is always applied regardless of this flag.
 */
export function useUrlManagerSync(
  eng: SearchEngine | null,
  readLocalStorage = false
): void {
  const currentHash = useRef('');

  useEffect(() => {
    if (!eng) return;

    const rawFragment = translateCfFragment(window.location.hash.slice(1));
    let initialFragment = stripNumberOfResults(rawFragment);

    if (!initialFragment && !rawFragment && readLocalStorage) {
      try {
        const stored = localStorage.getItem('coveo-standalone-search-box-data');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.value) {
            initialFragment = `q=${encodeURIComponent(data.value)}`;
          }
          localStorage.removeItem('coveo-standalone-search-box-data');
        }
      } catch {
        // ignore malformed data
      }
    }

    // Track the raw fragment (including numberOfResults if present) so popstate
    // comparison correctly detects back/forward navigation to the original URL.
    currentHash.current = rawFragment;

    const urlManager = buildUrlManager(eng, {
      initialState: { fragment: initialFragment },
    });

    // Apply numberOfResults from the raw URL hash if present.
    // registerNumberOfResults sets both numberOfResults AND defaultNumberOfResults,
    // so even if restoreSearchParameters is later called without numberOfResults
    // (e.g. on re-initialization), it falls back to this value instead of 24.
    applyNumberOfResultsFromFragment(rawFragment, eng);

    let firstSearchDone = false;
    let isRestoringFromPopState = false;

    const unsubscribe = urlManager.subscribe(() => {
      const fragment = urlManager.state.fragment;

      if (!firstSearchDone) {
        if (fragment) {
          firstSearchDone = true;
        } else if (initialFragment) {
          return;
        }
      }

      const newHash = fragment ? `#${fragment}` : '';

      if (newHash === window.location.hash) {
        currentHash.current = fragment;
        return;
      }

      // When the only difference between the new fragment and the current hash is that
      // numberOfResults is being removed, skip pushState entirely. Coveo's UrlManager
      // never serializes numberOfResults (getCoreActiveSearchParameters excludes it), so
      // this removal happens on every initial load that carries numberOfResults in the hash.
      // Calling pushState in that case causes Next.js App Router to treat it as a new
      // navigation, remounting the page with a clean URL where numberOfResults is absent,
      // which causes restoreSearchParameters to fall back to defaultNumberOfResults (24).
      // By skipping the pushState we keep the URL intact and avoid the navigation cycle.
      // We also leave currentHash.current unchanged so popstate comparison stays accurate.
      const strippedCurrent = stripNumberOfResults(
        window.location.hash.slice(1)
      );
      const normalizedCurrent = strippedCurrent ? `#${strippedCurrent}` : '';
      if (newHash === normalizedCurrent) return;

      currentHash.current = fragment;
      const fullUrl = `${window.location.pathname}${window.location.search}${newHash}`;
      if (isRestoringFromPopState) {
        window.history.replaceState(null, '', fullUrl);
      } else {
        window.history.pushState(null, '', fullUrl);
      }
      dispatchCoveoUrlChange();
    });

    function syncAndSearch(hash: string) {
      isRestoringFromPopState = true;
      urlManager.synchronize(hash);
      applyNumberOfResultsFromFragment(hash, eng!);
      const analyticsAction = loadSearchAnalyticsActions(
        eng!
      ).logSearchFromLink();
      eng!.dispatch(loadSearchActions(eng!).executeSearch(analyticsAction));
      currentHash.current = hash;
      setTimeout(() => {
        isRestoringFromPopState = false;
      }, 0);
    }

    const onPopState = () => {
      const hash = window.location.hash.slice(1);
      try {
        if (
          decodeURIComponent(currentHash.current) !== decodeURIComponent(hash)
        ) {
          syncAndSearch(hash);
        }
      } catch {
        if (currentHash.current !== hash) {
          syncAndSearch(hash);
        }
      }
    };

    window.addEventListener('popstate', onPopState);

    if (initialFragment) {
      const q = new URLSearchParams(initialFragment).get('q');
      if (q) {
        eng.dispatch(loadQueryActions(eng).updateQuery({ q }));
      }
    }

    const analyticsAction = loadSearchAnalyticsActions(eng).logInterfaceLoad();
    eng.dispatch(loadSearchActions(eng).executeSearch(analyticsAction));

    return () => {
      unsubscribe();
      window.removeEventListener('popstate', onPopState);
    };
  }, [eng, readLocalStorage]);
}
